import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SPLIT_CHANGE_KEYS, queryKeys } from '@splitfiesta/core';
import {
  type ClaimLineItemsDto,
  type ExpenseResponseDto,
  LINE_ITEMS_ERROR_KEYS,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

export function isLineItemsConflict(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    (error as { translationKey?: unknown }).translationKey ===
      LINE_ITEMS_ERROR_KEYS.conflict
  );
}

/**
 * The body is the caller's full set of picks, so a repeat is idempotent. The
 * caller gates on connectivity: a claim is never queued offline, and the
 * scope serializes rapid saves of the same expense. Claims commute, so a
 * conflict is resubmitted once against the refetched row.
 */
export function useClaimLineItemsMutation(expenseId: string, groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    networkMode: 'always',
    retry: false,
    scope: { id: `claim-${expenseId}` },
    mutationFn: async (body: ClaimLineItemsDto) => {
      const send = async () => {
        const response = await api.expenses.claimLineItems(expenseId, body);
        return (response.data ?? response) as ExpenseResponseDto;
      };
      try {
        return await send();
      } catch (error) {
        if (!isLineItemsConflict(error)) throw error;
        await queryClient
          .refetchQueries({ queryKey: queryKeys.expense(expenseId) })
          .catch(() => undefined);
        return send();
      }
    },
    onSuccess: expense => {
      queryClient.setQueryData(queryKeys.expense(expenseId), expense);
      // The response may trail a concurrent claim; the detail re-reads it.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.expense(expenseId),
      });
      for (const queryKey of SPLIT_CHANGE_KEYS(groupId)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: error => {
      if (isLineItemsConflict(error)) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.expense(expenseId),
        });
      }
    },
  });
}
